import React, { useCallback, useEffect, useState } from "react";
import profileService from "../../services/profileService";
import LoadingSpinner from "../common/LoadingSpinner";

function Stat({ label, value, tone = "neutral" }) {
  return (
    <div className={`health-stat health-tone-${tone}`}>
      <div className="health-stat-value">{value ?? "—"}</div>
      <div className="health-stat-label">{label}</div>
    </div>
  );
}

function SystemHealth() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [data, setData] = useState(null);
  const [lastLoaded, setLastLoaded] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await profileService.getSystemHealth();
      setData(res || null);
      setLastLoaded(new Date());
    } catch (e) {
      setError(e?.response?.data?.error || "Failed to load system health");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 30 * 1000);
    return () => clearInterval(t);
  }, [load]);

  if (loading && !data) return <LoadingSpinner message="Loading system health..." />;

  return (
    <div className="system-health">
      <div className="management-header">
        <div>
          <h2>System Health</h2>
          <p className="text-muted">
            Live snapshot. Refreshes every 30 seconds.
          </p>
        </div>
        <button className="btn btn-secondary" onClick={load} disabled={loading}>
          {loading ? (
            <>
              <i className="fas fa-spinner fa-spin" /> Refreshing...
            </>
          ) : (
            <>
              <i className="fas fa-sync-alt" /> Refresh
            </>
          )}
        </button>
      </div>

      {error && (
        <div className="error-message">
          <i className="fas fa-exclamation-circle" /> {error}
        </div>
      )}

      {data && (
        <>
          <h3 className="system-health-section">Database</h3>
          <div className="health-grid">
            <Stat label="Pool Active" value={data.db?.poolActive} tone="info" />
            <Stat label="Pool Total" value={data.db?.poolTotal} tone="neutral" />
          </div>

          <h3 className="system-health-section">Queues</h3>
          <div className="health-grid">
            <Stat label="Reminders Pending" value={data.queues?.remindersPending} tone="info" />
            <Stat label="Reminders Fired (24h)" value={data.queues?.remindersFired24h} tone="success" />
            <Stat
              label="Failed Emails (24h)"
              value={data.queues?.failedEmails24h}
              tone={data.queues?.failedEmails24h > 0 ? "danger" : "success"}
            />
          </div>

          <h3 className="system-health-section">Next Activities</h3>
          <div className="health-grid">
            <Stat label="Active" value={data.nextActivities?.active} tone="info" />
            <Stat
              label="Overdue"
              value={data.nextActivities?.overdue}
              tone={data.nextActivities?.overdue > 0 ? "warning" : "success"}
            />
          </div>

          <h3 className="system-health-section">Errors & Performance</h3>
          <div className="health-grid">
            <Stat label="System Errors (24h)" value={data.errors?.system24h} tone={data.errors?.system24h > 0 ? "warning" : "success"} />
            <Stat label="p95 Response (ms)" value={data.performance?.p95ResponseMs} tone="info" />
            <Stat label="Sample Size" value={data.performance?.sampleSize} tone="neutral" />
          </div>

          <h3 className="system-health-section">Server</h3>
          <div className="health-grid">
            <Stat label="Memory (MB)" value={data.system?.memoryMb} tone="neutral" />
            <Stat label="Memory Free (%)" value={data.system?.memoryFreePercent} tone="info" />
            <Stat label="Uptime (sec)" value={data.system?.uptimeSec} tone="neutral" />
          </div>

          <div className="health-footer">
            <i className="fas fa-clock" />
            Last updated: {lastLoaded ? lastLoaded.toLocaleTimeString() : "—"}
          </div>
        </>
      )}
    </div>
  );
}

export default SystemHealth;